import React, { useEffect, useMemo } from 'react';
import { Provider } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import Society from './scenes/Society/containers';
import Event from './scenes/Event/containers';

import reduxStore from './reduxStore';
import withSocket from './components/Session/withSocket';
import SocketContext from './components/Session/SocketContext';
import withAuthentication from './components/Session/withAuthentication';
import withTheme from './components/Theme/withTheme';
import NetworkDetector from './NetworkDetector';
import withPlatformInfoUpdates from './scenes/User/components/platformInfo/PlatformInfoContextProvider';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { ContainerProvider } from './scenes/VirtualModeration/features/virtualFeature/containers/main';
import CookieNoticeWrapper from './components/Session/CookieNoticeWrapper';
import { useTheme } from './components/Theme/ThemeContext';
import { ThemeProvider, createTheme, StyledEngineProvider } from '@mui/material/styles';

export const CONNECTION_ERROR_MESSAGE = 'Could not connect to server';
export const EVENT_NOT_FOUND_MESSAGE = 'AppState not found';
export const EVENT_NOT_CONFIGURED_MESSAGE = 'Navigation not configured';
export const USER_NOT_ATTENDING_PERMISSIONS = 'User not in attendees';

function App() {
    const { theme } = useTheme();

    // Setting Material UI ThemeProvider
    const materialUITheme = useMemo(() => {
        return createTheme({
            palette: {
                type: 'light', // we can later use dark/light modes
                primary: {
                    main: theme.primary || '#00A480',
                },
                secondary: {
                    main: '#EFEFEF',
                },
            },
            overrides: {
                MuiButton: {
                    styleOverrides: {
                        root: {
                            boxShadow: 'none',
                            textTransform: 'none',
                            backgroundColor: theme.primary || '#00A480',
                        },
                    },
                },
            },
        });
    }, [theme]);

    return (
        <ContainerProvider>
            <Provider store={reduxStore}>
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={materialUITheme}>
                        <Switch>
                            <Route path="/event/:eventName">
                                <Event />
                            </Route>
                            <Route path="/kiosk/:eventName">
                                <Event kiosk />
                            </Route>
                            <Route path="/">
                                <Society />
                            </Route>
                        </Switch>
                    </ThemeProvider>
                </StyledEngineProvider>
            </Provider>
        </ContainerProvider>
    );
}

//Pass set theme function in the app component
const AppWithSocketAndUpdates = props => {
    useEffect(() => {
        if (!props.waitingWorker) {
            serviceWorkerRegistration.register({
                onUpdate: reg => props.onServiceWorkerUpdate(reg),
                onSuccess: () => {},
            });
        }
    }, [props.newVersionAvailable]);

    return (
        <SocketContext.Consumer>
            {({ socket }) => socket && <App {...props} socket={socket} />}
        </SocketContext.Consumer>
    );
};

const AppWithRouter = withTheme(withPlatformInfoUpdates(AppWithSocketAndUpdates));

const AppWithCookiesEnabled = props => {
    return (
        <CookieNoticeWrapper>
            <AppWithRouter {...props} />
        </CookieNoticeWrapper>
    );
};

//Add Authentication context
export default NetworkDetector(withSocket(withAuthentication(AppWithCookiesEnabled)));
